/**
 * Centralized error handling utility for API calls and user interactions
 */

// Error types for better categorization
export const ERROR_TYPES = {
  AUTHENTICATION: 'authentication',
  NETWORK: 'network',
  VALIDATION: 'validation',
  PERMISSION: 'permission',
  SERVER: 'server',
  UNKNOWN: 'unknown'
};

// Common error messages for better UX
export const ERROR_MESSAGES = {
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  NO_PERMISSION: 'You do not have permission to perform this action.',
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  SERVER_ERROR: 'Server error. Please try again later.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.'
};

/**
 * Categorizes an error based on its properties
 * @param {Error} error - The error object
 * @returns {string} - The error type
 */
export const categorizeError = (error) => {
  if (!error) return ERROR_TYPES.UNKNOWN;
  
  // Check for authentication errors
  if (error.response?.status === 401 || 
      error.message?.includes('Session expired') ||
      error.message?.includes('Unauthorized') ||
      error.message?.includes('Authentication')) {
    return ERROR_TYPES.AUTHENTICATION;
  }
  
  // Check for permission errors
  if (error.response?.status === 403 || 
      error.message?.includes('permission') ||
      error.message?.includes('forbidden')) {
    return ERROR_TYPES.PERMISSION;
  }
  
  // Check for validation errors
  if (error.response?.status === 400 ||
      error.response?.status === 422 ||
      error.message?.includes('validation') ||
      error.message?.includes('invalid')) {
    return ERROR_TYPES.VALIDATION;
  }
  
  // Check for server errors
  if (error.response?.status >= 500 ||
      error.message?.includes('server') ||
      error.message?.includes('internal')) {
    return ERROR_TYPES.SERVER;
  }
  
  // Check for network errors
  if (error.code === 'NETWORK_ERROR' ||
      error.message?.includes('Network Error') ||
      error.message?.includes('timeout') ||
      !error.response) {
    return ERROR_TYPES.NETWORK;
  }
  
  return ERROR_TYPES.UNKNOWN;
};

/**
 * Gets a user-friendly error message based on the error
 * @param {Error} error - The error object
 * @returns {string} - User-friendly error message
 */
export const getUserFriendlyMessage = (error) => {
  if (!error) return ERROR_MESSAGES.UNKNOWN_ERROR;
  
  const errorType = categorizeError(error);
  
  // Try to get a specific message from the server response
  const serverMessage = error.response?.data?.detail || 
                       error.response?.data?.message || 
                       error.response?.data?.error;
  
  switch (errorType) {
    case ERROR_TYPES.AUTHENTICATION:
      return ERROR_MESSAGES.SESSION_EXPIRED;
    case ERROR_TYPES.PERMISSION:
      return ERROR_MESSAGES.NO_PERMISSION;
    case ERROR_TYPES.NETWORK:
      return ERROR_MESSAGES.NETWORK_ERROR;
    case ERROR_TYPES.SERVER:
      return serverMessage || ERROR_MESSAGES.SERVER_ERROR;
    case ERROR_TYPES.VALIDATION:
      return serverMessage || ERROR_MESSAGES.VALIDATION_ERROR;
    default:
      return serverMessage || error.message || ERROR_MESSAGES.UNKNOWN_ERROR;
  }
};

/**
 * Handles errors consistently across the application
 * @param {Error} error - The error object
 * @param {Object} handlers - Object containing handler functions
 * @param {Function} handlers.setError - Function to set error state
 * @param {Function} handlers.setShowLoginModal - Function to show login modal
 * @param {Function} handlers.setToastMessage - Function to show toast message
 * @param {Function} handlers.setShowToast - Function to control toast visibility
 * @param {string} context - Context where the error occurred (for logging)
 * @returns {Object} - Error handling result
 */
export const handleError = (error, handlers = {}, context = '') => {
  const errorType = categorizeError(error);
  const userMessage = getUserFriendlyMessage(error);
  
  // Log the error for debugging
  console.error(`Error in ${context}:`, {
    type: errorType,
    message: error.message,
    response: error.response?.data,
    stack: error.stack
  });
  
  // Handle authentication errors
  if (errorType === ERROR_TYPES.AUTHENTICATION && handlers.setShowLoginModal) {
    handlers.setShowLoginModal(true);
    if (handlers.setError) {
      handlers.setError('');
    }
    return { handled: true, requiresLogin: true, userMessage };
  }
  
  // Set error message in state if handler is provided
  if (handlers.setError) {
    handlers.setError(userMessage);
  }
  
  // Show toast message if handlers are provided
  if (handlers.setToastMessage && handlers.setShowToast) {
    handlers.setToastMessage(userMessage);
    handlers.setShowToast(true);
    setTimeout(() => handlers.setShowToast(false), 5000);
  }
  
  return { 
    handled: true, 
    requiresLogin: errorType === ERROR_TYPES.AUTHENTICATION,
    userMessage,
    errorType 
  };
};

/**
 * Creates a standardized error handler for API calls
 * @param {Object} handlers - Handler functions
 * @param {string} context - Context for logging
 * @returns {Function} - Error handler function
 */
export const createErrorHandler = (handlers, context) => {
  return (error) => handleError(error, handlers, context);
};

/**
 * Wraps an async function with error handling
 * @param {Function} asyncFn - The async function to wrap
 * @param {Object} handlers - Handler functions
 * @param {string} context - Context for error logging
 * @returns {Function} - Wrapped function with error handling
 */
export const withErrorHandling = (asyncFn, handlers, context) => {
  return async (...args) => {
    try {
      return await asyncFn(...args);
    } catch (error) {
      const result = handleError(error, handlers, context);
      throw error; // Re-throw so calling code can handle if needed
    }
  };
};
